/**
 * overlayKinds — THE OVERLAY KIND ROSTER. Data only.
 *
 * Every body-level floating surface in the app declares itself here as a KIND,
 * and a kind is the ONE place that answers three questions about it: which band
 * it stacks in, whether the engine positions it, and which policies the
 * primitive applies while it is open. `overlayRegistry.js` reads this roster to
 * order and stack entries; `overlayPlacement.js` reads it to build floating-ui's
 * middleware; `useOverlay.js` reads it to wire the outside-click and focus
 * policies. None of them holds a second opinion about a kind.
 *
 * A new pop-up is ONE line in `KIND_SPECS` below plus one `useOverlay` call.
 *
 * WHY THE BANDS ARE NAMED, AND WHY EACH RUNG IS DERIVED. Inherited verbatim from
 * the retired `components/universal/overlayLadder.js` (removed in th-e692n.1),
 * the module this roster replaced: a tier spelled as a bare `z-[…]` beside its
 * component is unreviewable, and two independently authored literals can
 * invert — they did, and th-dmi6q is what it cost (an inspector panel that
 * painted BEHIND the glance card it was opened from). Every rung below but the
 * two anchors is arithmetic on the rung beneath it, so moving one rung moves
 * everything above it and no pair can invert.
 *
 * THE ORDER, bottom to top (the magnitudes are the retired ladder's, unchanged):
 *
 *   99900   chrome    — search combobox, notifications, unsaved banner, spotlight FAB
 *   100000  hover     — the glance-card adapters, the citation hover card, the map popups
 *   100200  panel     — inspector, glance member popover, Artemis floating input
 *   100400  guidance  — narration bubble, tour player
 *   100500  cue       — dwell indicator, Artemis hold bow
 *   100600  tooltip   — floating-vue's popper layer, via `--th-overlay-z-tooltip`
 *   999999  modal     — Headless UI dialogs, the deletion preview and the first-open intro
 *
 * THREE kinds sit off the ladder entirely, and none paints a body-level box of
 * its own, so none has a stacking for anyone to choose. `dismissable` is the
 * Escape registrant (a draw session, the record walk, a form editor, a
 * selection clear) and paints nothing; `glance-card-stack` is a pinned glance
 * card's claim while its layer stack holds a layer, whose layers paint inside
 * the card's own box; `inline-menu` is a menu disclosed inside its host's own
 * box (the Dossier dock's More), which its host's band already stacks. Each
 * declares `band: null` — no rung, no rect, no z (design D6) — and they are the
 * ONLY kinds allowed to, which the roster's suite pins. `bandBase(null)` is a
 * refusal, so the registry never asks it.
 *
 * An open overlay's z is its band's base PLUS its activation index among the
 * open overlays of that band — that arithmetic lives in `overlayRegistry.js`,
 * not here. See "WHY THE RUNGS ARE A STRIDE APART" at the foot of this header.
 *
 * WHY THE CONSTANTS ARE COPIED, NOT IMPORTED. Every numeric option below is
 * transcribed from the module that owned it before retirement, with the source
 * named in a comment: `overlayLadder.js` and `elementInspectorLayer.js` were
 * both deleted in this change's retirement wave (design D8, th-e692n.1).
 * Importing them would have made the roster depend on files that no longer
 * exist. The ONE exception is the cursor kind's X clearance,
 * which imports `BOW_CURSOR_CARD_CLEARANCE_X`: that constant is a live contract
 * with `public/artemis-bow-cursor.svg` (its own SVG-pin ratchet re-derives it
 * when the glyph is redrawn), it is not retired, and it lives in a composable —
 * so the Ruby `GLANCE_REACH` gate, which bans a positioning adapter from
 * reaching into `components/glance/*`, is not in play.
 *
 * CHOICES MADE HERE, recorded so a reviewer need not re-derive them:
 *
 * 1. `kindFor` on a name the roster does not declare THROWS a `TypeError` naming
 *    the kind in development (Vite dev and vitest), and in a production build
 *    logs `console.error` and returns `null` so the caller leaves the surface
 *    UNREGISTERED rather than at an invented band. Both halves are the spec
 *    scenario "An unknown kind is refused". `bandBase` refuses an unknown band
 *    the same way, for symmetry.
 * 2. `autoUpdate` spells all FOUR of floating-ui 1.1.1's options explicitly.
 *    Three of them (`ancestorScroll`, `ancestorResize`, `elementResize`) default
 *    to TRUE in the library, so "`{ elementResize: true }` only" — the held
 *    cursor card's rule (design D2a) — is only true when the other three are
 *    written false. A kind the engine does not place carries `autoUpdate: false`
 *    outright: there is nothing to keep current.
 * 3. `placement` is declared here for `glance-cursor`, whose X-sidestep the
 *    design pins to the roster (D2a), for the two map popups and the map
 *    glance card, whose above-then-below-then-beside order the map design pins
 *    (v2-map-platform D10, D11a) — those four are the kinds carrying a
 *    `fallbackPlacements` list —
 *    and for the two `cue` kinds, whose offsets are measured FROM the box corner
 *    their placement picks and mean nothing apart from it. Every other kind
 *    takes its placement from its `useOverlay` call, which is where the
 *    primitive's signature puts it.
 * 4. Option KEYS are uniform where the concept is shared (`gap` = the anchor
 *    offset on the main axis, `crossGap` = the cross axis, `padding` = the
 *    viewport inset a shift clamps to), with the surface's own constant name
 *    cited beside it. Keys that are genuinely surface-specific keep their own
 *    names (`arrowHalf`, `flipClearance`, `edgeMarginRight`). Every option is a
 *    finite number but one: `flip`, which a kind that NEVER mirrors carries as
 *    the literal `false` — the two cues. A kind that mirrors only SOMETIMES
 *    passes it from the call site, which is what the held cursor card does.
 * 5. The HEIGHT BUDGET is chosen BY KIND, as a `budget` token from the closed
 *    set `BUDGET_TOKENS`, never as a function: this roster is data and imports
 *    no engine (the engine imports it). `useOverlay.js` maps each token to the
 *    engine's pre-pass — `'roomier-side'` is the rule the retired
 *    `elementInspectorLayer.js` carried (removed in th-e692n.1) — max(roomBelow,
 *    roomAbove) minus gap and padding — for the inspector and the member
 *    popover; `'lane'` is the Skyline lane's one-side budget; `'viewport'`
 *    is the cursor card's, and the citation hover card's, whole-viewport budget,
 *    and the map glance card's whole-container budget (its call's `boundary`
 *    makes the map container the area the budget measures);
 *    `null` is UNBOUNDED — every unplaced kind, the two cues, and the Artemis
 *    floating input, which is placed against its MEASURED height with no cap. A
 *    call site may still pass its own function; the token is what a kind gets
 *    when it passes none.
 *    A kind may also declare `readableHeight`, the least height its surface reads
 *    at. The engine then DECIDES its side before the chain runs, from the anchor
 *    and the viewport alone: the side asked for, unless its room cannot hold that
 *    height and the opposite side has more room. `useOverlay` also floors the box
 *    at it (`minHeight`, never past the cap), so the surface opens readable rather
 *    than as the chrome of a body still loading (th-kxn6c.12.36). The inspector —
 *    the Peek — is the one kind that declares it (founder ruling 26, th-kxn6c.12.26).
 * 6. AN ANCHOR OFFSET IS DECLARED IN ONE OF TWO CLOSED VOCABULARIES, never a
 *    mixture. PLACEMENT-bound is the default and what every kind but one takes:
 *    `gap` is the offset on the axis the placement runs along and `crossGap` the
 *    offset on the other, so both follow the placement when a flip or a sidestep
 *    turns a corner. AXIS-bound is `axisOffsets: { x, y }`, for a kind whose two
 *    clearances are different quantities that must each stay on their own screen
 *    axis: `glance-cursor` owes the bow glyph `BOW_CURSOR_CARD_CLEARANCE_X` on X
 *    and a 10 px pointer gap on Y, and the X clearance is what its `right`/`left`
 *    sidestep spends (`overlayPlacement.js` states the rule the engine reads it
 *    by). A kind declaring `axisOffsets` keeps `gap` for its OTHER reader —
 *    `avoidOverlays` clears an anchor and a blocker by it — and declares no
 *    `crossGap`, so no number is written twice.
 * 7. `hosts` IS THE PARENT SEAM'S RULE, and it is a claim about the DOM rather
 *    than about the ladder: true for a kind whose surface another overlay's
 *    component tree can render INSIDE, false for a kind that is only ever drawn
 *    NEAR something. `useOverlay.js` hands its entry holder down under
 *    `OVERLAY_PARENT_ENTRY` — the seam a nested surface reads to name its
 *    parent — only for a hosting kind. Every cue, chrome bar, guidance surface
 *    and tooltip is false, which is what stops an adopter high in the component
 *    tree (the workspace shell adopts the Artemis bow) from becoming the parent
 *    of every overlay opened beneath it. A non-hosting kind still INJECTS, so a
 *    cue drawn over a panel is still that panel's child; it simply hands
 *    nothing on, and its descendants reach the nearest hosting ancestor above.
 * 8. `holdsCards` IS THE GLANCE CARD'S POINTER-LEAVE CLAIM, and it is a trait
 *    rather than a band because the band is the wrong set. True for exactly the
 *    two panels a viewer crosses to FROM a card — the inspector and the member
 *    popover — and false for every other kind, the Artemis floating input
 *    included although it shares their `panel` band: it is summoned at the
 *    pointer, not opened from a card, so no pointer journey from a card to it
 *    needs a leave held. So is the member popover's Track Record sibling
 *    (`track-record-member-popover`), the same panel opened from a card back
 *    rather than a glance card. The one claimant that is NOT a panel is the
 *    card's own layer stack (`glance-card-stack`): a value inspected inside a
 *    pinned card opens in the card rather than beside it, and the card holds
 *    exactly as it held for the inspector it replaces.
 *    A CLAIMANT HOLDS ONLY THE CARDS IT WAS OPENED FROM — the entries in its
 *    parent chain, which the parent seam (choice 7) names with no plumbing. A
 *    panel relates to a card through that seam and no other way, so a Peek
 *    opened from a table cell or a map marker holds no card at all, and a card
 *    hovered while it is up closes on its own leave. `useGlanceCardPersistence.js`
 *    reads the claim per card as `anyOpen({ trait: 'holdsCards', within: card })`.
 *    Making another kind a claimant is this one line and nothing else.
 * 9. `supersedesCards` IS "ONE FLOATING PANEL AT A TIME", and only the inspector
 *    carries it. A panel that carries it, on opening, dismisses every open card
 *    (the `hover` band) it was NOT opened from, through that card's own
 *    `dismiss` (`overlayRegistry.js`'s `supersedeCards`, which `useOverlay.js`
 *    calls before the first placement). A card in its parent chain is its host
 *    and stays; a card hovered AFTER the panel opened registers later, so it is
 *    untouched here and closes on its own leave under choice 8. The member
 *    popover does not carry it: it opens from a roster row inside the one card
 *    the viewer is engaged with, which is its parent. A `hover` entry with no
 *    callable `dismiss` is never superseded, which is what keeps a map confirm
 *    (`map-confirm`, whose adopter registers none) open beside a Peek.
 *
 * WHY THE RUNGS ARE A STRIDE APART. Because z is band base plus activation
 * index, two rungs closer together than the number of overlays a band can hold
 * at once do not stack — they INTERLEAVE. `chrome` and `hover` were one apart,
 * so the second open chrome bar computed exactly `hover`'s base and the third
 * computed above it: four kinds share `chrome` and two of them (the search
 * combobox and the notifications overlay) are up together in the ordinary case,
 * which would have put a bar over every glance card — the inversion the
 * retired `GLANCE_CARD_Z_BAND` existed to prevent. So `BAND_STRIDE` is the
 * floor on the distance between adjacent rungs, and it is the ceiling the
 * registry asserts its activation index against; the roster's own suite holds
 * every consecutive pair at or above it. `chrome` therefore sits at 99900
 * rather than the retired `overlayLadder.js`'s 99999 (removed in th-e692n.1) —
 * one stride below `hover`, still above every other z in the app and still
 * below every glance card, so no surface changes order. Every other rung
 * keeps its magnitude exactly.
 */

import {
  BOW_CURSOR_CARD_CLEARANCE_X,
  BOW_OVERLAY_GAP,
  CURSOR_SHOULDER_ANCHOR,
  GLANCE_DWELL_RING_PX,
} from "@/composables/artemisCursorMetrics";

// ---------------------------------------------------------------------------
// BANDS
//
// `chrome` and `modal` are the two anchors; every rung between is an offset on
// the rung beneath it, so the ladder cannot invert. Each offset is at least
// `BAND_STRIDE`, so a band's activation indexes cannot reach the band above.
// The magnitudes are the ones the retired `overlayLadder.js` shipped (removed
// in th-e692n.1), except `chrome`, which drops to one stride below `hover`
// (see the header).
// ---------------------------------------------------------------------------

/**
 * The floor on the distance between two adjacent rungs: the most overlays one
 * band can hold open at once, with room. The registry adds an activation index
 * to a band's base, so a band may never reach the rung above it.
 */
export const BAND_STRIDE = 100;

const CHROME_Z = 99900; // one stride below HOVER_Z (was SHELL_CHROME_Z_BAND, 99999)
const HOVER_Z = CHROME_Z + BAND_STRIDE; // 100000 (was GLANCE_CARD_Z_BAND)
const PANEL_Z = HOVER_Z + 200; // 100200 (was ELEMENT_INSPECTOR_Z_BAND)
const GUIDANCE_Z = PANEL_Z + 200; // 100400 — the narration surfaces, above every panel
const CUE_Z = GUIDANCE_Z + 100; // 100500 (was GLANCE_DWELL_INDICATOR_Z_BAND)
const TOOLTIP_Z = CUE_Z + 100; // 100600 — floating-vue's rung, above every stateful panel
const MODAL_Z = 999999; // (was MODAL_Z_BAND, the top anchor)

/** Base z of each band. An entry's z is this plus its activation index. */
export const BAND_BASES = Object.freeze({
  chrome: CHROME_Z,
  hover: HOVER_Z,
  panel: PANEL_Z,
  guidance: GUIDANCE_Z,
  cue: CUE_Z,
  tooltip: TOOLTIP_Z,
  modal: MODAL_Z,
});

/**
 * The shadow each band's surfaces wear, as a whole Tailwind class, or `null`
 * for a band that casts none. Depth follows the overlay kind (app-design-standard
 * D5): a floating surface reads `shadow-lg`, a modal `shadow-xl`, and a `cue` is
 * a mark on the pointer, not a surface, so it has none. `useOverlay` returns the
 * entry as `depthClass`, so a host binds it and spells no shadow of its own.
 * Each value is a literal on purpose: Tailwind finds a class by scanning this
 * file, and cannot see one assembled from parts.
 */
export const BAND_DEPTH = Object.freeze({
  chrome: "shadow-lg",
  hover: "shadow-lg",
  panel: "shadow-lg",
  guidance: "shadow-lg",
  cue: null,
  tooltip: "shadow-lg",
  modal: "shadow-xl",
});

/** Every band, ordered LOW to HIGH. The order is the ladder. */
export const BANDS = Object.freeze([
  "chrome",
  "hover",
  "panel",
  "guidance",
  "cue",
  "tooltip",
  "modal",
]);

// ---------------------------------------------------------------------------
// SHARED PLACEMENT OPTIONS
//
// The defaults every anchored kind starts from, and the per-surface numbers
// the retired modules owned before this change removed them.
// ---------------------------------------------------------------------------

/**
 * ← the retired ELEMENT_INSPECTOR_EDGE_PADDING
 * (components/universal/elementInspectorLayer.js, removed in th-e692n.1)
 */
const EDGE_PADDING = 8;
/**
 * ← the retired ELEMENT_INSPECTOR_ANCHOR_GAP
 * (components/universal/elementInspectorLayer.js, removed in th-e692n.1)
 */
const ANCHOR_GAP = 4;

const SHARED_OPTIONS = { padding: EDGE_PADDING, gap: ANCHOR_GAP };

/**
 * The least height a Peek reads at: its header, its tab strip, about two rows of
 * body and its dock (founder ruling 26). Measured on the UAT window (1470×687,
 * run 2): the header 57 px, the tab strip 33, two body rows about 50, and the
 * dock 53 for a record or 87 for an area's two-row dock. Below this a Peek shows
 * its dock and little else. The Dossier shell declares none of those heights, so
 * this is the one number.
 */
const PEEK_READABLE_HEIGHT = 240;

/** ← OFFSET_Y (components/glance/GlanceCardCursor.vue) — pointer→card on the Y axis */
const CURSOR_OFFSET_Y = 10;
/** ← PADDING (components/glance/GlanceCardCursor.vue) — the viewport inset its clamp holds */
const CURSOR_PADDING = 10;
/**
 * The cursor card's two clearances, bound to the SCREEN AXES (header choice 6):
 * X is the bow glyph's painted reach, Y the pointer gap, and the sidestep onto
 * `right`/`left` spends the X one — which is `GlanceCardCursor.vue`'s retired
 * `OFFSET_X`/`OFFSET_Y` pair, each on the axis it was always named for.
 */
const CURSOR_AXIS_OFFSETS = Object.freeze({
  x: BOW_CURSOR_CARD_CLEARANCE_X,
  y: CURSOR_OFFSET_Y,
});

/** ← ARROW_GAP (components/property-collection/tooltips/MetadataLaneTooltip.vue) */
const LANE_ARROW_GAP = 8;
/** ← VIEWPORT_PADDING (components/property-collection/tooltips/MetadataLaneTooltip.vue) */
const LANE_VIEWPORT_PADDING = 10;
/** ← ARROW_HALF (…/MetadataLaneTooltip.vue) — the card triangle's half-width */
const LANE_ARROW_HALF = 6;
/** ← FLIP_CLEARANCE (…/MetadataLaneTooltip.vue) — extra room above the lane on a flip */
const LANE_FLIP_CLEARANCE = 12;

/** ← CURSOR_GAP (composables/useArtemisFloatingInput.js) — applied on BOTH axes */
const INPUT_CURSOR_GAP = 4;
/** ← EDGE_MARGIN_RIGHT of the retired composables/useArtemisFloatingInput.js (deleted in th-xle47.10) */
const INPUT_EDGE_MARGIN_RIGHT = 16;

// ---------------------------------------------------------------------------
// autoUpdate PRESETS — floating-ui 1.1.1 exposes exactly these four options.
// ---------------------------------------------------------------------------

/** Anchored panels: follow the reference through scroll and resize. */
const AUTO_UPDATE_ANCHORED = {
  ancestorScroll: true,
  ancestorResize: true,
  elementResize: true,
  animationFrame: false,
};

/**
 * A held cursor card: its reference is a rect captured at REVEAL, so ancestor
 * scroll and resize must NOT move it. Only the card's own growth re-clamps it.
 */
const AUTO_UPDATE_SELF_RESIZE_ONLY = {
  ancestorScroll: false,
  ancestorResize: false,
  elementResize: true,
  animationFrame: false,
};

// ---------------------------------------------------------------------------
// TRAIT DEFAULTS
//
// A kind spec below states only what differs from these. Every frozen roster
// entry still carries all of them.
// ---------------------------------------------------------------------------

const KIND_DEFAULTS = {
  band: null,
  /** Other overlays avoid this one. */
  obstacle: true,
  /** This one avoids other overlays. */
  avoids: false,
  /** It avoids the overlay it was opened FROM (false for a card hosted inside a panel). */
  avoidsParent: true,
  /**
   * Another overlay's component tree can render INSIDE this surface, so it
   * hands its entry down as their parent (header choice 7). False — the
   * default — for a kind nothing is ever opened from.
   */
  hosts: false,
  /**
   * While it is open, the pointer-leave of every glance card in its parent chain
   * is held and replayed on its close (header choice 8). False — the default —
   * for every kind but the two panels a viewer crosses to from a card and the
   * card's own layer stack.
   */
  holdsCards: false,
  /**
   * On opening, it dismisses every open card it was not opened from (header
   * choice 9). False — the default — for every kind but the inspector.
   */
  supersedesCards: false,
  /** Dismissable through the registry's Escape list. */
  escape: false,
  /** `'panel'` | `'capture'` | `false`. */
  dismissOnOutside: false,
  trapFocus: false,
  /** The engine positions it; `false` = it registers for stacking and obstacles only. */
  placed: true,
  /** It carries a floating-ui `arrow` middleware. */
  arrow: false,
  /** Primary placement, when the roster pins one. */
  placement: null,
  /** The height budget's token (`BUDGET_TOKENS`); `null` = unbounded. */
  budget: null,
};

/** The three enum values `dismissOnOutside` may take. */
export const DISMISS_ON_OUTSIDE_POLICIES = Object.freeze(["panel", "capture", false]);

/**
 * The closed set a kind's `budget` is drawn from. `useOverlay.js` owns the
 * mapping from token to the engine's pre-pass function (header choice 5).
 */
export const BUDGET_TOKENS = Object.freeze(["roomier-side", "lane", "viewport", null]);

// ---------------------------------------------------------------------------
// THE ROSTER
// ---------------------------------------------------------------------------

/** The roster popover's traits, shared by its two hosts (`KIND_SPECS` below). */
const MEMBER_POPOVER = {
  band: "panel",
  obstacle: true,
  avoids: true,
  // Its composer opens the mention picker inside it.
  hosts: true,
  // Opened from a roster row inside a card, and portaled out of it.
  holdsCards: true,
  escape: true,
  dismissOnOutside: false,
  trapFocus: false,
  budget: "roomier-side",
};

/**
 * The map popups' traits, shared by both (`KIND_SPECS` below; v2-map-platform
 * D11a). Anchored at the host's one map-point anchor and placed inside the map
 * container (the engine's `boundary` option, which the adopter passes), above
 * the point, then below, then beside it, capped at the roomier side's room. Each
 * is an obstacle a Peek lands clear of, and avoids one already open. Neither
 * ever moves the map to fit: a point near an edge is answered by the flip and
 * the shift, never by a pan.
 */
const MAP_POPUP = {
  band: "hover",
  placed: true,
  obstacle: true,
  avoids: true,
  placement: "top",
  fallbackPlacements: ["bottom", "right", "left"],
  budget: "roomier-side",
};

const KIND_SPECS = {
  // --- chrome: the workspace's own bars. Never placed, never dismissable, but
  // an opaque bar a panel must not open underneath, so each is an obstacle. ---
  "search-combobox": { band: "chrome", placed: false, obstacle: true, avoids: false, escape: false },
  "notifications-overlay": { band: "chrome", placed: false, obstacle: true, avoids: false, escape: false },
  "unsaved-banner": { band: "chrome", placed: false, obstacle: true, avoids: false, escape: false },
  "spotlight-launcher": { band: "chrome", placed: false, obstacle: true, avoids: false, escape: false },

  // --- hover: the glance card, on each adapter that places one. ---
  /**
   * The cursor-follow card. Anchored at the REVEAL rect, not the live pointer,
   * and re-clamped only by its own growth — so the sidestep is a fallback list
   * here rather than arithmetic in the adapter. The ordered candidate list is
   * `bottom-start` (below-right of the pointer), then `top-start` (the opposite
   * vertical side), then `right` and `left` — the two CENTRED placements, which
   * are exactly the X-sidestep's `y = cursorY − height / 2`.
   *
   * Its offsets are AXIS-bound (header choice 6): the sidestep spends the bow
   * clearance on X and nothing on the cross axis, so the centring floating-ui
   * already performs is what puts the card level with the pointer.
   */
  "glance-cursor": {
    band: "hover",
    obstacle: true,
    avoids: true,
    avoidsParent: true,
    hosts: true,
    escape: true,
    placed: true,
    placement: "bottom-start",
    fallbackPlacements: ["top-start", "right", "left"],
    autoUpdate: AUTO_UPDATE_SELF_RESIZE_ONLY,
    budget: "viewport",
    options: {
      // `gap` is here for `avoidOverlays`, which clears an anchor and a blocker
      // by it; the PLACEMENT reads `axisOffsets` and never this key.
      gap: CURSOR_OFFSET_Y,
      padding: CURSOR_PADDING,
      axisOffsets: CURSOR_AXIS_OFFSETS,
    },
  },
  /** The Skyline metadata lane's card: the one kind that carries an arrow. */
  "glance-lane": {
    band: "hover",
    obstacle: true,
    avoids: true,
    avoidsParent: true,
    hosts: true,
    escape: true,
    placed: true,
    arrow: true,
    budget: "lane",
    options: {
      gap: LANE_ARROW_GAP,
      padding: LANE_VIEWPORT_PADDING,
      arrowHalf: LANE_ARROW_HALF,
      flipClearance: LANE_FLIP_CLEARANCE,
    },
  },
  /**
   * The map card (v2-map-platform D10). Anchored at the host's one map-point
   * anchor and placed inside the map container (its call's `boundary`) in the
   * map popups' order — above the point, then below, then beside it on the
   * roomier side — and capped at the container's height, so a card near an
   * edge flips and shifts inward and never needs a clamp of its own.
   */
  "glance-popup": {
    band: "hover",
    obstacle: true,
    avoids: true,
    avoidsParent: true,
    hosts: true,
    escape: true,
    placed: true,
    placement: MAP_POPUP.placement,
    fallbackPlacements: MAP_POPUP.fallbackPlacements,
    budget: "viewport",
  },
  /**
   * The citation hover card, which renders INSIDE the inspector. It must not
   * avoid the panel that hosts it, or it is pushed out of its own host.
   */
  "citation-hover-card": {
    band: "hover",
    obstacle: true,
    avoids: true,
    avoidsParent: false,
    hosts: true,
    escape: true,
    placed: true,
    budget: "viewport",
  },
  /**
   * A confirm popup on the map: the Artemis pin's and the parcel ring's. It
   * closes only through its own action — no Escape, no outside click — and its
   * adopter (`MapAnchoredPopup`) registers it with NO `dismiss`, so a Peek's
   * card supersession (header choice 9) passes it by and the directive keeps
   * its confirm.
   */
  "map-confirm": { ...MAP_POPUP, escape: false, dismissOnOutside: false },
  /**
   * An informational popup on the map: the avatar cluster's count. Escape and a
   * click anywhere outside it (a map click included) close it, and so does a
   * Peek opening, since it registers its `dismiss`.
   */
  "map-info": { ...MAP_POPUP, escape: true, dismissOnOutside: "panel" },

  // --- panel: the surfaces opened from a card, a cell or the pointer. ---
  /** The portaled element inspector: the one kind that traps Tab. */
  inspector: {
    band: "panel",
    obstacle: true,
    avoids: true,
    avoidsParent: true,
    hosts: true,
    // Holds only the cards it was opened from (header choice 8): the Peek a value
    // outside a card opens (a cell, a marker, a row) holds none. Inside a glance
    // card a clicked value pushes a layer into the card instead.
    holdsCards: true,
    // One floating panel at a time (header choice 9): opening closes every card
    // it was not opened from.
    supersedesCards: true,
    escape: true,
    dismissOnOutside: "panel",
    trapFocus: true,
    budget: "roomier-side",
    // Its side is decided before the chain (header choice 5): below its anchor,
    // unless the room below cannot hold a readable Peek and the room above is larger.
    options: { readableHeight: PEEK_READABLE_HEIGHT },
  },
  /**
   * The roster popover, opened from a glance card's roster. NO outside-click
   * and NO Tab trap, deliberately: a composer must not vanish on a stray click,
   * and the mention picker owns Tab.
   *
   * It AVOIDS, like the inspector and for the same reason. Its probe sits in a
   * roster row INSIDE the card it opens from, so a panel placed on the probe
   * alone lands on top of that card; `avoidsParent` is left at its default
   * `true` precisely so the host card — the entry the parent seam hands it — is
   * one of the obstacles it walks out of. That is the
   * `roster-connection-messaging` scenario "The popover is placed clear of the
   * card". The surface it is nested inside is its ANCHOR, not its host: unlike
   * the citation hover card, it is portaled to `body` and nothing about it
   * renders within the card's box.
   */
  "member-popover": MEMBER_POPOVER,
  /**
   * The SAME popover opened from a Track Record card back's people, which sit
   * on no glance card: it holds no card's pointer-leave, so a map card beneath
   * it dismisses normally (`roster-connection-messaging` scenario "A Track
   * Record pop-up never pins map cards"). Every other trait is the roster
   * popover's.
   */
  "track-record-member-popover": { ...MEMBER_POPOVER, holdsCards: false },
  /**
   * The Artemis floating input. Its reference is the pointer AT OPEN, frozen —
   * so no autoUpdate — and its Escape stays LOCAL because the contract stands
   * down on an editable target.
   */
  "artemis-floating-input": {
    band: "panel",
    obstacle: true,
    avoids: false,
    // Its editor opens the mention picker inside it.
    hosts: true,
    escape: false,
    dismissOnOutside: "capture",
    trapFocus: false,
    autoUpdate: false,
    options: {
      gap: INPUT_CURSOR_GAP,
      crossGap: INPUT_CURSOR_GAP,
      padding: INPUT_EDGE_MARGIN_RIGHT,
    },
  },

  // --- guidance: the tour surfaces. They keep their own shared positioning
  // composable (edge-aligned flip, viewport-centred when no anchor resolves),
  // so they register for stacking and obstacles only. ---
  "narration-bubble": { band: "guidance", placed: false, obstacle: true, escape: false },
  /**
   * The tour player: the one compact tour surface, whose combined states (a
   * primary state plus badges for other preparing or ready tours) share this one
   * registration. A stray click leaves it alone, and it traps no focus. Its
   * Escape PAUSES the tour, and only while a tour plays: the adopter passes an
   * `escapeWhen` getter to the registry, so a paused, ready or preparing player
   * hands Escape to the next dismissable overlay.
   */
  "tour-player": {
    band: "guidance",
    placed: false,
    obstacle: true,
    dismissOnOutside: false,
    trapFocus: false,
    escape: true,
  },

  // --- cue: pointer ornaments. Never obstacles (nothing should move out of a
  // cue's way) and never dismissable (there is nothing to dismiss).
  //
  // BOTH ARE PLACED AGAINST A 0×0 VIRTUAL ELEMENT AT THE POINTER (design D2),
  // so a cue's `placement` decides WHICH corner of its own box lands on the
  // pointer and `gap`/`crossGap` carry the rest of the anchoring. The two are
  // one contract, so both halves are declared here rather than the placement at
  // the call site (the exception the header's choice 3 names): the sign of a
  // cue's offsets is unreadable without the corner they are measured from.
  //
  // `flip: false` on both, because a cue never mirrors — it is a mark ON the
  // cursor, and the mirrored side is where the cursor glyph itself paints. That
  // also turns `shift`'s cross axis on, which is what keeps a cue at the
  // viewport's edge clamped inside it (by the shared `padding`) instead of cut
  // off. `autoUpdate: false`: neither box grows, a virtual reference has no
  // scroll ancestors of its own, and the adopter re-places on pointer movement
  // by calling `replace()` — the primitive's rule for a rect that mutates in
  // place. ---
  "dwell-indicator": {
    band: "cue",
    obstacle: false,
    avoids: false,
    escape: false,
    placed: true,
    autoUpdate: false,
    // `bottom-start` lands the box's TOP-LEFT on the pointer, so each offset is
    // the ring's centre anchor less half its box.
    placement: "bottom-start",
    options: {
      gap: CURSOR_SHOULDER_ANCHOR.y - GLANCE_DWELL_RING_PX / 2,
      crossGap: CURSOR_SHOULDER_ANCHOR.x - GLANCE_DWELL_RING_PX / 2,
      flip: false,
    },
  },
  "artemis-bow": {
    band: "cue",
    obstacle: false,
    avoids: false,
    escape: false,
    placed: true,
    autoUpdate: false,
    // `top-end` lands the badge's BOTTOM-RIGHT corner on the pointer — the box
    // side is floating-ui's own to subtract — so the only number left is the
    // buffer, owed up and left, which is `+gap` on a `top` main axis and
    // `−crossGap` on the cross one.
    placement: "top-end",
    options: {
      gap: BOW_OVERLAY_GAP,
      crossGap: -BOW_OVERLAY_GAP,
      flip: false,
    },
  },

  // --- tooltip: floating-vue's popper layer, bridged in. floating-vue places
  // these, so the roster claims no placement. It binds no keydown at all — its
  // one Escape is a keyUP on the popper root, heard only while focus is inside
  // it — so a shown menu takes its turn in Escape order here, and the bridge's
  // `dismiss` runs floating-vue's own Escape hide (floatingVueObserver.js). ---
  "floating-vue-popper": {
    band: "tooltip",
    placed: false,
    obstacle: true,
    avoids: false,
    escape: true,
  },

  // --- modal: Headless UI owns placement, focus and Escape for both. Each is
  // a surface with content of its own, so a tooltip or a card opened inside one
  // names the dialog its parent rather than whatever sits behind it. ---
  dialog: { band: "modal", placed: false, obstacle: true, escape: false, hosts: true },
  "deletion-preview": {
    band: "modal",
    placed: false,
    obstacle: true,
    escape: false,
    hosts: true,
  },
  // The first-open intro (launch-marketing-pages M10): a modal with no Headless
  // UI behind it, so the registry owns its Escape; full-viewport, so no obstacle.
  intro: { band: "modal", placed: false, obstacle: false, escape: true },

  // --- OFF THE LADDER: the non-visual dismissables (the draw sessions, the
  // record walk, the scalar form editor, the two We settings forms whose
  // `active` is unconditionally true, the selection clear, the tool-sequence
  // cancel). They register to take a turn in Escape order and nothing else, so
  // they carry NO band: no rung, no rect, no z, and never an obstacle — there
  // is no box for another overlay to move out of the way of (design D6). ---
  dismissable: {
    band: null,
    placed: false,
    obstacle: false,
    avoids: false,
    avoidsParent: false,
    escape: true,
    dismissOnOutside: false,
    trapFocus: false,
  },
  /**
   * A pinned glance card's layer stack while it holds a layer
   * (element-inspector-redesign D6, th-kxn6c.8.4): the card's `holdsCards`
   * claim, and nothing else. The layers render INSIDE the card's own box, which
   * the card's adapter already registers, so this entry has no box, no z and no
   * Escape turn (each layer takes its own, through `useEscapeDismiss`). It exists
   * so the card holds while a layer is open exactly as it held while the
   * inspector it replaces was open.
   */
  "glance-card-stack": {
    band: null,
    placed: false,
    obstacle: false,
    avoids: false,
    avoidsParent: false,
    holdsCards: true,
    escape: false,
    dismissOnOutside: false,
    trapFocus: false,
  },
  /**
   * A menu disclosed INSIDE its host's own box — the Dossier dock's More, which
   * holds the deletion proposals (th-kxn6c.12.10). It is not portaled: it paints
   * in the Peek, the card or the docked pane it sits in, which that host's own
   * band already stacks, so it has no z and no rect, and it is no obstacle. What
   * it takes from the primitive is a turn in Escape order (it opens after its
   * host, so one Escape closes the menu and the next its host) and the `panel`
   * outside-click policy, tested against the menu itself, so a press anywhere
   * else in the host — a tab, a value that pushes a layer — closes it.
   */
  "inline-menu": {
    band: null,
    placed: false,
    obstacle: false,
    avoids: false,
    avoidsParent: false,
    escape: true,
    dismissOnOutside: "panel",
    trapFocus: false,
  },
};

/**
 * Builds one frozen roster entry: trait defaults, then the spec's overrides,
 * with `options` MERGED over the shared defaults (never replacing them) and
 * every nested object and array frozen in place.
 */
function freezeKind(name, spec) {
  const {
    options: optionOverrides,
    autoUpdate: autoUpdateOverride,
    fallbackPlacements,
    ...traits
  } = spec;

  const placed = traits.placed ?? KIND_DEFAULTS.placed;
  // A kind the engine does not place has no position to keep current.
  const autoUpdate =
    autoUpdateOverride !== undefined ? autoUpdateOverride : placed ? AUTO_UPDATE_ANCHORED : false;

  return Object.freeze({
    ...KIND_DEFAULTS,
    ...traits,
    name,
    placed,
    fallbackPlacements: fallbackPlacements ? Object.freeze([...fallbackPlacements]) : null,
    autoUpdate: autoUpdate === false ? false : Object.freeze({ ...autoUpdate }),
    options: Object.freeze({ ...SHARED_OPTIONS, ...optionOverrides }),
  });
}

/** The frozen roster, keyed by kind name. The one answer to which kinds exist. */
export const OVERLAY_KINDS = Object.freeze(
  Object.fromEntries(
    Object.entries(KIND_SPECS).map(([name, spec]) => [name, freezeKind(name, spec)]),
  ),
);

/**
 * True in Vite's dev server and under vitest, false in a production build.
 * Mirrors `useFocusEntity.js`'s gate — declared locally there too, for the same
 * reason: four lines are not worth widening a module's public surface. The
 * try/catch is for node environments with no `import.meta.env` at all.
 */
function isDevMode() {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env) {
      return Boolean(import.meta.env.DEV) || import.meta.env.MODE !== "production";
    }
  } catch {
    // import.meta.env unavailable — fall through to NODE_ENV.
  }
  const nodeProcess = globalThis.process;
  return Boolean(nodeProcess) && nodeProcess.env?.NODE_ENV !== "production";
}

/**
 * The kind a surface registers under.
 *
 * A name the roster does not declare is REFUSED: it throws in development, so
 * the gap is fixed in the change that opened it, and in production it is logged
 * and answered with `null`, so the caller leaves the surface unregistered rather
 * than painting it at a band nobody chose.
 */
export function kindFor(name) {
  if (Object.prototype.hasOwnProperty.call(OVERLAY_KINDS, name)) return OVERLAY_KINDS[name];

  const message =
    `Unknown overlay kind ${JSON.stringify(name)}. Every body-level overlay declares ` +
    `itself in composables/overlay/overlayKinds.js; add a kind there rather than ` +
    `stacking this surface by hand.`;
  if (isDevMode()) throw new TypeError(message);
  console.error(message);
  return null;
}

/**
 * The base z of a band, before the activation index the registry adds. Refuses
 * an unknown band exactly as `kindFor` refuses an unknown kind.
 */
export function bandBase(band) {
  if (Object.prototype.hasOwnProperty.call(BAND_BASES, band)) return BAND_BASES[band];

  const message =
    `Unknown overlay band ${JSON.stringify(band)}. The ladder's rungs are ` +
    `${BANDS.join(", ")}, declared in composables/overlay/overlayKinds.js.`;
  if (isDevMode()) throw new TypeError(message);
  console.error(message);
  return null;
}

/**
 * The shadow class of a band, or `null` when it casts none. An off-ladder kind
 * (`band: null`) has no surface of its own to lift, so it answers `null` too.
 * It reads a band a roster entry already carries, so it refuses nothing:
 * `kindFor` and `bandBase` are where an undeclared name is turned away.
 */
export function bandDepth(band) {
  return Object.prototype.hasOwnProperty.call(BAND_DEPTH, band) ? BAND_DEPTH[band] : null;
}

/**
 * Publishes the tooltip rung as a CSS custom property so the floating-vue bridge
 * stylesheet can seat `.v-popper__popper` on the ladder without duplicating the
 * number. Called once, from `entrypoints/application.js`, right after the plugin
 * is installed. This is the ONLY place in this module that touches the DOM.
 */
export function installOverlayLadder(root = document.documentElement) {
  if (!root || !root.style) return;
  root.style.setProperty("--th-overlay-z-tooltip", String(BAND_BASES.tooltip));
}
